import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  MapPin,
  Radio,
  Search,
  Navigation,
  Settings2,
  Building2,
  Cable,
  XCircle,
  Wifi,
  Ruler,
  BarChart3,
  Loader2,
  ChevronDown,
  ArrowLeftRight
} from "lucide-react";
import LocationSearchBar from "./LocationSearchBar";
import { checkServiceCoverage } from "../utils/serviceCoverageUtils";
import "../styles/MapControlsDrawer.css";

const drawerVariants = {
  hidden: {
    y: "100%",
    opacity: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
    },
  },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
      mass: 0.8,
    },
  },
};

export default function MapControlsDrawer({
  // Search Panel Props
  lat,
  lng,
  address,
  reverseAddress,
  addressOptions,
  loadingAddress,
  loadingReverse,
  error,
  onLatChange,
  onLngChange,
  onAddressChange,
  onAddressSearch,
  onLatLngSearch,
  onAddressSelection,
  // Location Props (Province/City)
  selectedProvince,
  selectedCity,
  onProvinceSelect,
  onCitySelect,
  // FAT Coverage Props
  coverageData,
  isCalculating,
  fatCoverageError,
  onFATCalculate,
  // Service Provider Coverage Props
  serviceProviderPolygons,
  // Cabling Rules Props
  onApplyCablingRules,
  onCancelCablingRules,
  isCablingRulesRunning,
  cablingProgress,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("search"); // "search" or "coverage"
  const [detectionMode, setDetectionMode] = useState("auto"); // Default to Auto (smart priority)
  const [selectedRange, setSelectedRange] = useState(300); // Selected range in meters
  const [visionModel, setVisionModel] = useState("gemini-2.5-flash"); // Vision AI model selection
  const [maxDropLength, setMaxDropLength] = useState(300); // Maximum acceptable drop length in meters
  const [maxStreetWidth, setMaxStreetWidth] = useState(6); // Maximum street width threshold in meters

  const handleFATCalculate = () => {
    const fatLat = parseFloat(lat);
    const fatLng = parseFloat(lng);
    if (!isNaN(fatLat) && !isNaN(fatLng)) {
      onFATCalculate(fatLat, fatLng, selectedRange, detectionMode, visionModel);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        className="drawer-trigger-button"
        onClick={() => setIsOpen(true)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <MapPin className="drawer-trigger-icon" />
        <span>کنترل‌های نقشه</span>
      </motion.button>

      {/* Drawer Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="drawer-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />

            {/* Drawer Content */}
            <motion.div
              className="drawer-container"
              variants={drawerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {/* Header */}
              <div className="drawer-header">
                <div className="drawer-title-group">
                  <div className="drawer-icon-wrapper">
                    <MapPin className="drawer-icon" />
                  </div>
                  <h2 className="drawer-title">کنترل‌های نقشه</h2>
                </div>
                <button
                  className="drawer-close-button"
                  onClick={() => setIsOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Tabs */}
              <div className="drawer-tabs">
                <button
                  className={`drawer-tab ${activeTab === "search" ? "active" : ""}`}
                  onClick={() => setActiveTab("search")}
                >
                  <MapPin size={16} />
                  جستجوی مکان
                </button>
                <button
                  className={`drawer-tab ${activeTab === "coverage" ? "active" : ""}`}
                  onClick={() => setActiveTab("coverage")}
                >
                  <Radio size={16} />
                  پوشش FAT
                </button>
              </div>

              {/* Content */}
              <div className="drawer-content">
                <AnimatePresence mode="wait">
                  {activeTab === "search" ? (
                    <motion.div
                      key="search"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                      className="drawer-panel"
                    >
                      {/* Search Panel Content */}
                      {error && (
                        <div className="drawer-error">
                          {error}
                        </div>
                      )}

                      <div className="drawer-section">
                        <h3 className="drawer-section-title">انتخاب استان و شهر</h3>
                        <LocationSearchBar
                          onProvinceSelect={onProvinceSelect}
                          onCitySelect={onCitySelect}
                          selectedProvince={selectedProvince}
                          selectedCity={selectedCity}
                        />
                      </div>

                      <div className="drawer-section">
                        <h3 className="drawer-section-title">
                          <Search size={14} />
                          جستجوی آدرس
                        </h3>
                        <div className="drawer-input-wrapper">
                          <Search className="drawer-input-icon" size={16} />
                          <input
                            type="text"
                            className="drawer-input with-icon"
                            placeholder="آدرس"
                            value={address}
                            onChange={(e) => onAddressChange(e.target.value)}
                            disabled={loadingAddress}
                          />
                        </div>
                        <button
                          className="drawer-button primary"
                          onClick={onAddressSearch}
                          disabled={loadingAddress}
                        >
                          {loadingAddress ? (
                            <>
                              <Loader2 className="drawer-button-spinner" size={16} />
                              در حال جستجو...
                            </>
                          ) : (
                            <>
                              <Search size={16} />
                              جستجو
                            </>
                          )}
                        </button>
                      </div>

                      <div className="drawer-section">
                        <h3 className="drawer-section-title">
                          <Navigation size={14} />
                          جستجوی مختصات
                        </h3>
                        <div className="drawer-input-row">
                          <input
                            type="text"
                            className="drawer-input"
                            placeholder="عرض جغرافیایی (lat)"
                            value={lat}
                            onChange={(e) => onLatChange(e.target.value)}
                          />
                          <input
                            type="text"
                            className="drawer-input"
                            placeholder="طول جغرافیایی (lng)"
                            value={lng}
                            onChange={(e) => onLngChange(e.target.value)}
                          />
                        </div>
                        <button
                          className="drawer-button primary"
                          onClick={onLatLngSearch}
                        >
                          <MapPin size={16} />
                          نمایش نقطه
                        </button>
                      </div>

                      <div className="drawer-section">
                        <h3 className="drawer-section-title">
                          <MapPin size={14} />
                          آدرس انتخاب‌شده
                        </h3>
                        <div className="drawer-input-wrapper">
                          {loadingReverse ? (
                            <Loader2 className="drawer-input-icon spinning" size={16} />
                          ) : (
                            <MapPin className="drawer-input-icon" size={16} />
                          )}
                          <input
                            type="text"
                            className="drawer-input with-icon readonly"
                            placeholder={loadingReverse ? "در حال دریافت آدرس..." : "آدرس نقطه انتخاب‌شده"}
                            value={reverseAddress}
                            readOnly
                          />
                        </div>
                      </div>

                      {addressOptions.length > 0 && (
                        <div className="drawer-section">
                          <h3 className="drawer-section-title">نتایج جستجو</h3>
                          <div className="drawer-options">
                            {addressOptions.map((opt, i) => (
                              <button
                                key={i}
                                className="drawer-option-button"
                                onClick={() => onAddressSelection(opt)}
                              >
                                {opt.address}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="coverage"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="drawer-panel"
                    >
                      {/* FAT Coverage Panel Content */}
                      {fatCoverageError && (
                        <div className="drawer-error">
                          {fatCoverageError}
                        </div>
                      )}

                      <div className="drawer-section">
                        <h3 className="drawer-section-title">
                          <Radio size={14} />
                          مختصات FAT
                        </h3>
                        <div className="drawer-input-row">
                          <input
                            type="text"
                            className="drawer-input"
                            placeholder="عرض جغرافیایی (lat)"
                            value={lat}
                            onChange={(e) => onLatChange(e.target.value)}
                          />
                          <input
                            type="text"
                            className="drawer-input"
                            placeholder="طول جغرافیایی (lng)"
                            value={lng}
                            onChange={(e) => onLngChange(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="drawer-section">
                        <h3 className="drawer-section-title">
                          <Building2 size={14} />
                          روش تشخیص ساختمان
                        </h3>
                        <div className="drawer-select-wrapper">
                          <select
                            className="drawer-select"
                            value={detectionMode}
                            onChange={(e) => setDetectionMode(e.target.value)}
                          >
                            <option value="auto">خودکار (Auto)</option>
                            <option value="microsoft">Microsoft Building Footprints</option>
                            <option value="sam3">SAM 3</option>
                            <option value="vision-llm-single">Vision AI (Single Image)</option>
                            <option value="vision-llm-dual">Vision AI (Dual Image)</option>
                          </select>
                          <ChevronDown className="drawer-select-icon" size={16} />
                        </div>
                      </div>

                      {/* Vision AI Model Selection - Only show for vision-llm-single and vision-llm-dual modes */}
                      {(detectionMode === "vision-llm-single" || detectionMode === "vision-llm-dual") && (
                        <div className="drawer-section">
                          <h3 className="drawer-section-title">
                            <Settings2 size={14} />
                            مدل Vision AI
                          </h3>
                          <div className="drawer-select-wrapper">
                            <select
                              className="drawer-select"
                              value={visionModel}
                              onChange={(e) => setVisionModel(e.target.value)}
                            >
                              <option value="gemini-2.5-flash">Gemini 2.5 Flash (سریع و کم‌هزینه)</option>
                              <option value="gemini-2.5-pro">Gemini 2.5 Pro (دقیق‌تر و گران‌تر)</option>
                            </select>
                            <ChevronDown className="drawer-select-icon" size={16} />
                          </div>
                        </div>
                      )}

                      <div className="drawer-section">
                        <h3 className="drawer-section-title">
                          <Settings2 size={14} />
                          عملیات پوشش
                        </h3>

                        {/* Horizontal Button Row */}
                        <div className="drawer-action-grid">
                          <button
                            className="drawer-action-button primary"
                            onClick={handleFATCalculate}
                            disabled={isCalculating || !lat || !lng}
                          >
                            {isCalculating ? (
                              <>
                                <Loader2 className="drawer-button-spinner" size={18} />
                                محاسبه...
                              </>
                            ) : (
                              <>
                                <Radio size={18} />
                                محاسبه منطقه تحت پوشش
                              </>
                            )}
                          </button>

                          <button
                            className="drawer-action-button secondary"
                            onClick={() => {
                              const latNum = parseFloat(lat);
                              const lngNum = parseFloat(lng);
                              if (!isNaN(latNum) && !isNaN(lngNum)) {
                                if (!serviceProviderPolygons) {
                                  alert('داده‌های پوشش سرویس در حال بارگذاری است. لطفاً چند لحظه صبر کنید.');
                                  return;
                                }
                                const result = checkServiceCoverage(latNum, lngNum, serviceProviderPolygons);
                                alert(`${result.provider}:\n${result.message}`);
                              } else {
                                alert('لطفاً ابتدا یک نقطه را انتخاب کنید');
                              }
                            }}
                            disabled={!lat || !lng}
                          >
                            <Wifi size={18} />
                            بررسی وضعیت سرویس‌دهی نقطه
                          </button>

                          {!isCablingRulesRunning ? (
                            <button
                              className="drawer-action-button success"
                              onClick={() => onApplyCablingRules(maxDropLength, maxStreetWidth)}
                              disabled={isCalculating || !coverageData}
                            >
                              <Cable size={18} />
                              اعمال قوانین کابل‌کشی
                            </button>
                          ) : (
                            <button
                              className="drawer-action-button danger"
                              onClick={onCancelCablingRules}
                            >
                              <XCircle size={18} />
                              لغو عملیات
                            </button>
                          )}
                        </div>

                        {/* MAX_DROP_LENGTH Input Section */}
                        <div className="drawer-setting-card">
                          <label className="drawer-setting-label">
                            <Ruler size={14} />
                            حداکثر طول دراپ قابل قبول (متر):
                          </label>
                          <input
                            type="number"
                            className="drawer-input"
                            value={maxDropLength}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              if (!isNaN(value) && value > 0) {
                                setMaxDropLength(value);
                              }
                            }}
                            min="50"
                            max="1000"
                            step="10"
                          />
                          <div className="drawer-setting-hint">
                            مقدار پیش‌فرض: 300 متر
                          </div>
                        </div>

                        {/* MAX_STREET_WIDTH Input Section */}
                        <div className="drawer-setting-card">
                          <label className="drawer-setting-label">
                            <ArrowLeftRight size={14} />
                            حداکثر عرض معبر قابل عبور (متر):
                          </label>
                          <input
                            type="number"
                            className="drawer-input"
                            value={maxStreetWidth}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (!isNaN(value) && value > 0) {
                                setMaxStreetWidth(value);
                              }
                            }}
                            min="2"
                            max="20"
                            step="0.5"
                          />
                          <div className="drawer-setting-hint">
                            مقدار پیش‌فرض: 6 متر (اگر معبر پهن‌تر باشد، ساختمان‌های آنطرف معبر سرویس‌دهی نمی‌شوند)
                          </div>
                        </div>
                      </div>

                      <div className="drawer-section">
                        <h3 className="drawer-section-title">
                          <Ruler size={14} />
                          شعاع پوشش: {selectedRange}m
                        </h3>
                        <div className="drawer-range-buttons">
                          {[100, 200, 300, 400, 500].map((range) => (
                            <button
                              key={range}
                              className={`drawer-range-button ${selectedRange === range ? 'active' : ''}`}
                              onClick={() => setSelectedRange(range)}
                            >
                              {range}m
                            </button>
                          ))}
                        </div>
                      </div>

                      {coverageData && (
                        <div className="drawer-section">
                          <h3 className="drawer-section-title">
                            <BarChart3 size={14} />
                            آمار پوشش
                          </h3>
                          <div className="drawer-stats-grid">
                            <div className="drawer-stat-item">
                              <div className="drawer-stat-icon">
                                <Building2 size={16} />
                              </div>
                              <div className="drawer-stat-content">
                                <span className="drawer-stat-label">کل ساختمان‌ها</span>
                                <span className="drawer-stat-value">{coverageData.stats?.totalBuildings || 0}</span>
                              </div>
                            </div>
                            <div className="drawer-stat-item success">
                              <div className="drawer-stat-icon">
                                <Building2 size={16} />
                              </div>
                              <div className="drawer-stat-content">
                                <span className="drawer-stat-label">تحت پوشش</span>
                                <span className="drawer-stat-value">{coverageData.stats?.coveredBuildings || 0}</span>
                              </div>
                            </div>
                            <div className="drawer-stat-item accent">
                              <div className="drawer-stat-icon">
                                <BarChart3 size={16} />
                              </div>
                              <div className="drawer-stat-content">
                                <span className="drawer-stat-label">درصد پوشش</span>
                                <span className="drawer-stat-value">{coverageData.stats?.coveragePercentage || 0}%</span>
                              </div>
                            </div>
                            <div className="drawer-stat-item">
                              <div className="drawer-stat-icon">
                                <MapPin size={16} />
                              </div>
                              <div className="drawer-stat-content">
                                <span className="drawer-stat-label">مساحت پوشش</span>
                                <span className="drawer-stat-value">{coverageData.stats?.coverageAreaSqM?.toLocaleString() || 0} m²</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {isCalculating && (
                        <div className="drawer-loading">
                          <div className="drawer-spinner"></div>
                          <span>در حال محاسبه پوشش...</span>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
